import { useRef, useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import ChartContainer from './ChartContainer';
import { useTheme } from '../../hooks/useTheme';
import {
  createChartAnimations,
  createHoverEffects,
  createEnhancedTooltips,
  createStaggeredAnimation,
  createInteractionEffects,
  ChartInteractionHandler
} from '../../utils/chartUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function SpendingTrendsChart({ data, loading = false, timeframe = 'monthly', onDataPointClick }) {
  const chartRef = useRef();
  const [selectedTimeframe, setSelectedTimeframe] = useState(timeframe);
  const [highlightedSeries, setHighlightedSeries] = useState(null);
  const [interactionHandler] = useState(() => new ChartInteractionHandler(chartRef, {
    enableDetailedViews: true,
    enableContextMenu: true
  }));
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [selectedDataPoints, setSelectedDataPoints] = useState([]);
  const { theme } = useTheme();

  // Force chart update when theme changes
  useEffect(() => {
    if (chartRef.current) {
      const chart = chartRef.current;
      // Update chart colors based on current theme
      chart.options.plugins.legend.labels.color = getComputedStyle(document.documentElement).getPropertyValue('--text').trim();
      chart.options.scales.x.ticks.color = getComputedStyle(document.documentElement).getPropertyValue('--muted').trim();
      chart.options.scales.y.ticks.color = getComputedStyle(document.documentElement).getPropertyValue('--muted').trim();
      chart.options.scales.x.grid.color = getComputedStyle(document.documentElement).getPropertyValue('--border').trim();
      chart.options.scales.y.grid.color = getComputedStyle(document.documentElement).getPropertyValue('--border').trim();
      chart.update('none');
    }
  }, [theme]);

  // Use real data or empty structure if no data provided
  const generateDefaultData = () => {
    return {
      labels: [],
      datasets: []
    };
  };

  const chartData = data || generateDefaultData();

  const colors = [
    { bg: 'rgba(139, 69, 19, 0.1)', border: 'rgba(139, 69, 19, 1)' }, // Brown
    { bg: 'rgba(34, 197, 94, 0.1)', border: 'rgba(34, 197, 94, 1)' },   // Green
    { bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 1)' }, // Yellow
    { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 1)' },   // Red
    { bg: 'rgba(168, 85, 247, 0.1)', border: 'rgba(168, 85, 247, 1)' }  // Purple
  ];

  // Initialize interaction handlers
  useEffect(() => {
    if (interactionHandler) {
      // Register detailed view for data points
      interactionHandler.registerDetailView('default', ({ elementIndex, datasetIndex, chartData }) => {
        const timePoint = chartData.labels[elementIndex];
        const department = chartData.datasets[datasetIndex].department;
        const value = chartData.datasets[datasetIndex].data[elementIndex];

        return {
          type: 'trend-detail',
          data: {
            timePoint,
            department,
            value,
            trend: calculateTrend(datasetIndex, elementIndex)
          }
        };
      });

      // Register context menu for trend analysis
      interactionHandler.registerContextMenu('default', ({ elementIndex, datasetIndex, position }) => {
        console.log('Trend context menu:', { elementIndex, datasetIndex, position });
        // Could show options like:
        // - Compare with previous period
        // - Set trend alert
        // - Export trend data
        // - Forecast future values
      });
    }
  }, [interactionHandler]);

  // Calculate trend for a specific data point
  const calculateTrend = (datasetIndex, elementIndex) => {
    const dataset = chartData?.datasets?.[datasetIndex];
    if (!dataset || elementIndex === 0) return null;

    const currentValue = dataset.data[elementIndex];
    const previousValue = dataset.data[elementIndex - 1];

    if (previousValue === 0) return null;

    const change = ((currentValue - previousValue) / previousValue) * 100;
    return {
      change: change.toFixed(1),
      direction: change >= 0 ? 'up' : 'down',
      magnitude: Math.abs(change)
    };
  };

  // Enhanced data point click handler
  const handleDataPointClick = (event, elements) => {
    if (elements.length > 0) {
      const elementIndex = elements[0].index;
      const datasetIndex = elements[0].datasetIndex;

      // Use advanced click handler
      const result = interactionHandler.handleAdvancedClick(event, elements, chartData, {
        onDrillDown: (elemIndex, dsIndex, data) => {
          const clickedData = {
            timePoint: data.labels[elemIndex],
            department: data.datasets[dsIndex].department,
            value: data.datasets[dsIndex].data[elemIndex],
            trend: calculateTrend(dsIndex, elemIndex)
          };

          if (onDataPointClick) {
            onDataPointClick(clickedData);
          }

          return clickedData;
        }
      });

      // Handle multi-select with Ctrl/Cmd key
      if (event.ctrlKey || event.metaKey) {
        const pointId = `${datasetIndex}-${elementIndex}`;
        setSelectedDataPoints(prev =>
          prev.includes(pointId)
            ? prev.filter(id => id !== pointId)
            : [...prev, pointId]
        );
      }
    }
  };

  // Handle legend clicks to highlight/unhighlight series
  const handleLegendClick = (event, legendItem, legend) => {
    const datasetIndex = legendItem.datasetIndex;
    const chart = legend.chart;

    if (highlightedSeries === datasetIndex) {
      // Unhighlight - show all series normally
      setHighlightedSeries(null);
      chart.data.datasets.forEach((dataset, index) => {
        dataset.borderColor = colors[index % colors.length].border;
        dataset.backgroundColor = colors[index % colors.length].bg;
      });
    } else {
      // Highlight selected series, fade others
      setHighlightedSeries(datasetIndex);
      chart.data.datasets.forEach((dataset, index) => {
        if (index === datasetIndex) {
          dataset.borderColor = colors[index % colors.length].border;
          dataset.backgroundColor = colors[index % colors.length].bg;
        } else {
          dataset.borderColor = colors[index % colors.length].border.replace('1)', '0.3)');
          dataset.backgroundColor = colors[index % colors.length].bg.replace('0.1)', '0.05)');
        }
      });
    }

    chart.update('none');
  };

  const isDark = theme === 'dark';
  const textColor = isDark ? '#f8fafc' : '#1f2937';
  const mutedColor = isDark ? '#94a3b8' : '#6b7280';
  const borderColor = isDark ? '#334155' : '#e2e8f0';
  const panelColor = isDark ? '#1e293b' : '#ffffff';

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    ...createStaggeredAnimation(80),
    ...createHoverEffects({
      enableGlow: true,
      enableCrosshair: true,
      glowColor: isDark ? 'rgba(96, 165, 250, 0.3)' : 'rgba(59, 130, 246, 0.3)'
    }),
    ...createInteractionEffects({
      enableRipple: true,
      enableTrails: true
    }),
    ...createEnhancedTooltips({
      label: function (context) {
        const value = context.parsed.y;
        return `${context.dataset.label}: ₹${value.toLocaleString('en-IN')}`;
      },
      afterLabel: function (context) {
        const dataIndex = context.dataIndex;
        const prevValue = context.dataset.data[dataIndex - 1];
        if (prevValue !== undefined) {
          const change = ((context.parsed.y - prevValue) / prevValue * 100).toFixed(1);
          const trend = change >= 0 ? '↗' : '↘';
          return `${trend} ${Math.abs(change)}% from previous period`;
        }
        return '';
      },
      footer: function (tooltipItems) {
        return 'Click to view detailed breakdown';
      }
    }),
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: textColor,
          font: {
            size: 12
          },
          usePointStyle: true,
          pointStyle: 'circle'
        },
        onClick: handleLegendClick
      },
      title: {
        display: false
      }
    },
    scales: {
      x: {
        ticks: {
          color: mutedColor,
          maxRotation: 45
        },
        grid: {
          color: borderColor,
          display: false
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: mutedColor,
          callback: function (value) {
            return '₹' + (value / 1000) + 'K';
          }
        },
        grid: {
          color: borderColor
        }
      }
    },
    onClick: handleDataPointClick,
    elements: {
      point: {
        radius: 4,
        hoverRadius: 8,
        borderWidth: 2,
        hoverBorderWidth: 3
      },
      line: {
        tension: 0.3,
        borderWidth: 3
      }
    }
  };

  const chartDataConfig = {
    labels: chartData?.labels || [],
    datasets: (chartData?.datasets || []).map((dataset, index) => ({
      label: dataset.department,
      data: dataset.data,
      backgroundColor: highlightedSeries !== null && highlightedSeries !== index
        ? colors[index % colors.length].bg.replace('0.1)', '0.05)')
        : colors[index % colors.length].bg,
      borderColor: highlightedSeries !== null && highlightedSeries !== index
        ? colors[index % colors.length].border.replace('1)', '0.3)')
        : colors[index % colors.length].border,
      borderWidth: 3,
      fill: true,
      pointBackgroundColor: colors[index % colors.length].border,
      pointBorderColor: panelColor,
      pointHoverBackgroundColor: colors[index % colors.length].border,
      pointHoverBorderColor: panelColor,
      pointHoverBorderWidth: 3,
      pointHoverRadius: 8,
      // Enhanced point styling for selected data points
      pointRadius: chartData?.datasets?.[0]?.data?.map((_, index) => {
        return selectedDataPoints.some(pointId => pointId.endsWith(`-${index}`)) ? 6 : 4;
      }) || 4,
      pointBorderWidth: chartData?.datasets?.[0]?.data?.map((_, index) => {
        return selectedDataPoints.some(pointId => pointId.endsWith(`-${index}`)) ? 3 : 2;
      }) || 2
    }))
  };

  // Timeframe selector options
  const timeframeOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  const handleTimeframeChange = (newTimeframe) => {
    if (newTimeframe !== selectedTimeframe) {
      setIsTransitioning(true);
      setSelectedTimeframe(newTimeframe);

      // Simulate data transition animation
      setTimeout(() => {
        setIsTransitioning(false);
      }, 500);

      // Here you would typically fetch new data based on the timeframe
    }
  };

  if (loading) {
    return (
      <ChartContainer
        title="Spending Trends"
        subtitle="Track spending patterns over time"
        loading={true}
        chartRef={chartRef}
        chartData={chartDataConfig}
      >
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--primary)' }}></div>
        </div>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer
      title="Spending Trends"
      subtitle="Click on data points for detailed breakdown • Click legend to highlight series"
      chartRef={chartRef}
      chartData={chartDataConfig}
      enableAnimations={true}
    >
      {/* Timeframe Selector */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {timeframeOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => handleTimeframeChange(option.value)}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${selectedTimeframe === option.value ? 'text-white' : 'hover:opacity-80'
              }`}
            style={{
              background: selectedTimeframe === option.value ? 'var(--primary)' : 'var(--border)',
              color: selectedTimeframe === option.value ? 'white' : 'var(--text)'
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="h-64">
        <Line ref={chartRef} data={chartDataConfig} options={options} />
      </div>

      {/* Trend Summary */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        {(chartData?.datasets || []).map((dataset, index) => {
          const currentValue = dataset.data[dataset.data.length - 1];
          const previousValue = dataset.data[dataset.data.length - 2];
          const change = previousValue ? ((currentValue - previousValue) / previousValue * 100).toFixed(1) : 0;
          const isPositive = change >= 0;

          return (
            <div
              key={dataset.department}
              className="p-3 rounded-lg border text-center"
              style={{
                background: 'var(--panel)',
                borderColor: 'var(--border)'
              }}
            >
              <div className="text-sm" style={{ color: 'var(--muted)' }}>
                {dataset.department}
              </div>
              <div className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
                ₹{currentValue?.toLocaleString('en-IN')}
              </div>
              <div className={`text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {isPositive ? '↗' : '↘'} {Math.abs(change)}%
              </div>
            </div>
          );
        })}
      </div>
    </ChartContainer>
  );
}

export default SpendingTrendsChart;